import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Checkbox, IconButton, SvgIcon, Tooltip } from "@mui/material";

import "./style.scss";
import { ReactComponent as CheckboxIcon } from "../../../assets/checkbox.icon.svg";
import { ReactComponent as CheckboxCheckedIcon } from "../../../assets/checkbox-checked.icon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/delete.icon.svg";
import { ReactComponent as EditIcon } from "../../../assets/edit.icon.svg";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import { ReactComponent as PlusIcon } from "../../../assets/plus.icon.svg";
import {
    deleteKnowledgeBase,
    fetchKnowledgeBasesSummary,
    forceUpdate,
    patchKnowledgeBase,
    rechunkKnowledgeBase,
    redigestLegacy,
    reextractMetadata,
    reindexKnowledgeBase
} from "../../../api/admin/knowledge-base/endpoints-referrers";
import { KnowledgeBase } from "../../../common/interfaces/admin/knowledge-base.interface";
import { UserRole } from "../../../common/enums/user-role.enum";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import HeaderLayout from "../../../components/layout/header-layout.comp";
import { LoaderView } from "../../../components/loader/loader.comp";
import { ToastContainer } from "../../../components/toast/toast.comp";
import { ActionMenu, CheckboxMenuFilterT } from "../../../components/menu-filter/menu-filter.comp";
import { MenuItem } from "../../../common/interfaces/comps/menu-item.interface";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../../components/confirmation-popup/confirmation-popup.comp";
import DigestionPolicyEditor from "../../../components/digestion-policy-editor/digestion-policy-editor.comp";
import { ModalTemplate } from "../../../components/modal-template/modal-template.comp";
import { AssistantEditDialog } from "../../../components/admin/assistant-edit-dialog/assistant-edit-dialog.comp";
import AddKnowledgeBaseView from "../add-knowledge-base/add-knowledge-base";

type BulkOp =
    | "reextract"
    | "rechunk"
    | "redigest"
    | "reindex"
    | "forceUpdate"
    | "delete"
    | "hide"
    | "unhide";

const BULK_META: Record<
    BulkOp,
    { title: string; actionName: string; description: string; describe: (n: number) => string }
> = {
    reextract: {
        title: "Re-extract metadata",
        actionName: "Re-extract",
        description:
            "Re-run the AI metadata pass over every stored document using the saved digestion policy, and re-stamp all chunks. One AI call per document.",
        describe: n =>
            `This re-runs the metadata extraction pass (one AI call per document) over every stored document in ${n} Assistant(s), using each Assistant's SAVED policy, re-stamps all chunks, and refreshes facets and filters. It may take a minute or two per Assistant.`
    },
    rechunk: {
        title: "Re-chunk documents",
        actionName: "Re-chunk",
        description:
            "Re-chunk and re-embed every current-pipeline document, then run the knowledge base update. Embedding cost per chunk.",
        describe: n =>
            `This re-chunks and re-embeds every current-pipeline document in ${n} Assistant(s) (embedding cost per chunk) and runs the knowledge base update. It may take several minutes on a large corpus.`
    },
    redigest: {
        title: "Re-digest legacy documents",
        actionName: "Re-digest",
        description:
            "Re-recognize up to 10 legacy (single-page era) documents per Assistant through the rich-document pipeline. Recognition cost per page.",
        describe: n =>
            `This re-recognizes up to 10 legacy documents per Assistant (recognition cost per page) through the rich-document pipeline, across ${n} Assistant(s). Run it again to continue in batches.`
    },
    reindex: {
        title: "Reindex",
        actionName: "Reindex",
        description:
            "Delete the search index and rebuild it — every document re-chunks and re-embeds from scratch.",
        describe: n =>
            `This deletes the search index of ${n} Assistant(s) and re-chunks and re-embeds every document (embedding cost per chunk). It may take a while on a large corpus.`
    },
    forceUpdate: {
        title: "Force update",
        actionName: "Update",
        description:
            "Run the knowledge base update pipeline now, processing any documents flagged with updates.",
        describe: n =>
            `This runs the knowledge base update pipeline for ${n} Assistant(s), processing any documents flagged with updates.`
    },
    delete: {
        title: "Delete Assistants",
        actionName: "Delete",
        description:
            "Remove the checked Assistants with all their documents, sources, and chat history. This cannot be undone.",
        describe: n =>
            `This permanently deletes ${n} Assistant(s) with all of their documents, sources, and chat history. This cannot be undone!`
    },
    hide: {
        title: "Hide Assistants",
        actionName: "Hide",
        description:
            "Remove the checked Assistants from chat. Documents, processing, and statistics are unaffected; unhide them here any time.",
        describe: n =>
            `This hides ${n} Assistant(s) from chat: users will no longer see or chat with them. Documents, processing, and statistics are unaffected, and you can unhide them from this list.`
    },
    unhide: {
        title: "Unhide Assistants",
        actionName: "Unhide",
        description: "Make the checked hidden Assistants available for chat again.",
        describe: n => `This makes ${n} hidden Assistant(s) available for chat again.`
    }
};

// Tenant admin-feature permission behind each maintenance op (§6). Hide and
// Unhide have no entry on purpose: they are role-gated to company admins,
// never tenant-permission-gated (hide_assistants_design.md D2).
const BULK_FEATURES: Partial<Record<BulkOp, string>> = {
    reextract: "actionReextract",
    rechunk: "actionRechunk",
    redigest: "actionRedigest",
    reindex: "actionReindex",
    forceUpdate: "actionForceUpdate",
    delete: "assistantDelete"
};

// Type filter (hide_assistants_design.md D6): the one option folds hidden
// Assistants into the list; unchecked, they are excluded entirely.
const TYPE_FILTER_ITEMS: MenuItem<string>[] = [{ label: "Hidden", value: "hidden" }];

/**
 * Redirect shim for the retired per-Assistant settings routes
 * (/customization, /assistant-settings, /prompt-tuning,
 * /search-configuration, /manage-invites): bookmarks land on the Assistants
 * list with the edit dialog deep-linked to the equivalent tab of the
 * previously-current Assistant.
 */
export function RedirectToAssistants({ tab }: { tab: string }) {
    const { user } = FirebaseAuthContainer.useContainer();
    // On a hard load the auth container is still resolving — navigating now
    // would drop the deep-link (no current KB yet). Wait for the user.
    if (!user) return null;
    const target = user.currentKnowledgeBaseId
        ? `/assistants?assistant=${user.currentKnowledgeBaseId}&tab=${tab}`
        : "/assistants";
    return <Navigate to={target} replace />;
}

export default function AssistantsView() {
    const {
        user,
        isSuperAdmin,
        hasAdminFeature,
        updateUserKnowledgeBase,
        loadKnowledgeBases,
        refreshKnowledgeBase,
        loadKnowledgeBaseConfig
    } = FirebaseAuthContainer.useContainer();
    const { showToast } = ToastContainer.useContainer();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const companyId = user?.companyId;
    // D8 (Phase C): the route is manager-protected; company admins keep the
    // full surface, per-KB managers get a scoped read of it (rows they
    // manage, Edit + Files only — the cost-bearing ops are admin+ per D11).
    const isCompanyAdmin = !!isSuperAdmin || user?.role === UserRole.admin;
    // Tenant admin-feature gates (tenant_admin_permissions_design.md §6);
    // hasAdminFeature is false for managers and bypasses for super admins.
    const canCreate = hasAdminFeature("assistantCreate");
    const canPolicy = hasAdminFeature("digestionPolicy");

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [assistants, setAssistants] = useState<KnowledgeBase[]>([]);
    const [checked, setChecked] = useState<KnowledgeBase[]>([]);
    const [bulkOp, setBulkOp] = useState<BulkOp | null>(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [editTarget, setEditTarget] = useState<KnowledgeBase | null>(null);
    const [editInitialTab, setEditInitialTab] = useState<string | null>(null);
    const [policyTarget, setPolicyTarget] = useState<KnowledgeBase | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [checkedTypes, setCheckedTypes] = useState<MenuItem<string>[]>([]);
    const includeHidden = checkedTypes.some(t => t.value === "hidden");
    // Toggling the Type filter can remove checked rows from view — clear the
    // selection so select-all math and bulk targets stay coherent.
    const setTypeFilter = useCallback((list: MenuItem<string>[]) => {
        setCheckedTypes(list);
        setChecked([]);
    }, []);

    const reload = useCallback(async () => {
        if (!companyId) return;
        setIsLoading(true);
        try {
            const res = await fetchKnowledgeBasesSummary(companyId);
            setAssistants(res.data?.items ?? []);
            setChecked([]);
        } catch {
            showToast("Failed to load Assistants", 3000);
        } finally {
            setIsLoading(false);
        }
    }, [companyId, showToast]);

    useEffect(() => {
        reload();
    }, [reload]);

    // Managers see only the Assistants they manage; the list endpoint returns
    // every KB the user belongs to (it also feeds the switcher), so scope
    // here. Hidden Assistants show only when the Type filter includes them
    // (hide_assistants_design.md D6), and the list sorts alphabetically.
    const visibleAssistants = useMemo(() => {
        const scoped = isCompanyAdmin
            ? assistants
            : assistants.filter(kb => kb.role === UserRole.manager || kb.role === UserRole.admin);
        const typed = includeHidden ? scoped : scoped.filter(kb => !kb.hidden);
        return [...typed].sort((a, b) =>
            (a.displayName || a.name).localeCompare(b.displayName || b.name, undefined, {
                sensitivity: "base"
            })
        );
    }, [assistants, isCompanyAdmin, includeHidden]);

    // Deep link from the retired routes: /assistants?assistant=<uid>&tab=<tab>
    // opens the edit dialog on that Assistant once the list has loaded.
    useEffect(() => {
        const uid = searchParams.get("assistant");
        if (!uid || !visibleAssistants.length) return;
        const target = visibleAssistants.find(a => a.uid === uid);
        if (target) {
            setEditInitialTab(searchParams.get("tab"));
            setEditTarget(target);
        }
        setSearchParams({}, { replace: true });
    }, [visibleAssistants, searchParams, setSearchParams]);

    const isChecked = useCallback(
        (kb: KnowledgeBase) => checked.some(c => c.uid === kb.uid),
        [checked]
    );

    // Toggle by uid, not object reference — after a reload the row objects are
    // new instances (DESIGN_GUIDE.md §4 list-action standard).
    const toggleChecked = useCallback(
        (kb: KnowledgeBase) =>
            setChecked(prev =>
                prev.some(c => c.uid === kb.uid)
                    ? prev.filter(c => c.uid !== kb.uid)
                    : [...prev, kb]
            ),
        []
    );

    const toggleSelectAll = useCallback(
        () =>
            setChecked(prev => (prev.length === visibleAssistants.length ? [] : visibleAssistants)),
        [visibleAssistants]
    );

    const runBulk = async () => {
        const op = bulkOp;
        setBulkOp(null);
        if (!companyId || !op || !checked.length) return;
        setBulkBusy(true);
        let ok = 0;
        let failed = 0;
        // Sequential on purpose: each operation is heavy on the flow side and
        // a parallel burst across Assistants can trip LLM rate limits.
        for (const kb of bulkTargets(op)) {
            try {
                if (op === "reextract") await reextractMetadata(companyId, kb.uid);
                else if (op === "rechunk") await rechunkKnowledgeBase(companyId, kb.uid);
                else if (op === "redigest") await redigestLegacy(companyId, kb.uid, 10);
                else if (op === "reindex") await reindexKnowledgeBase(companyId, kb.uid);
                else if (op === "delete") await deleteKnowledgeBase(companyId, kb.uid);
                else if (op === "hide")
                    await patchKnowledgeBase(companyId, kb.uid, { hidden: true });
                else if (op === "unhide")
                    await patchKnowledgeBase(companyId, kb.uid, { hidden: false });
                else await forceUpdate(companyId, kb.uid);
                ok += 1;
            } catch {
                failed += 1;
            }
        }
        setBulkBusy(false);
        showToast(
            `${BULK_META[op].title} complete for ${ok} Assistant(s)` +
                (failed ? `, ${failed} failed` : "") +
                ".",
            4000
        );
        // These change what the chat switcher and the list itself show.
        if (op === "delete" || op === "hide" || op === "unhide") {
            loadKnowledgeBases(companyId);
            reload();
        }
    };

    const openFiles = async (kb: KnowledgeBase) => {
        if (user?.currentKnowledgeBaseId !== kb.uid) {
            await updateUserKnowledgeBase(kb.uid);
        }
        navigate("/knowledge-base");
    };

    // Each maintenance op (Delete included) is its own tenant permission; the
    // menu offers only the permitted ones and disappears (with the checkbox
    // column) when none remain (DESIGN_GUIDE §4: no actions → no checkboxes).
    // Hide is always offered to company admins (never permission-gated) and
    // Unhide joins it while the selection includes a hidden Assistant.
    const bulkActions = useMemo(() => {
        const ops = (
            ["reextract", "rechunk", "redigest", "reindex", "forceUpdate", "delete"] as BulkOp[]
        ).filter(op => hasAdminFeature(BULK_FEATURES[op]!));
        if (isCompanyAdmin) {
            ops.push("hide");
            if (checked.some(c => c.hidden)) ops.push("unhide");
        }
        return ops.map(op => ({
            name: BULK_META[op].title,
            description: BULK_META[op].description,
            action: () => {
                // The company must keep at least one Assistant.
                if (op === "delete" && checked.length >= assistants.length) {
                    showToast("At least one Assistant must remain — leave one unchecked.", 4000);
                    return;
                }
                if (op === "hide") {
                    const visibleTotal = assistants.filter(a => !a.hidden).length;
                    const checkedVisible = checked.filter(c => !c.hidden).length;
                    if (checkedVisible === 0) {
                        showToast("The checked Assistants are already hidden.", 4000);
                        return;
                    }
                    // ... and at least one visible for chat (design D4).
                    if (checkedVisible >= visibleTotal) {
                        showToast(
                            "At least one Assistant must remain visible — leave one unchecked.",
                            4000
                        );
                        return;
                    }
                }
                setBulkOp(op);
            }
        }));
    }, [hasAdminFeature, isCompanyAdmin, checked, assistants, showToast]);
    const showBulk = bulkActions.length > 0;
    // Hide/Unhide act on the applicable subset of the selection.
    const bulkTargets = useCallback(
        (op: BulkOp) =>
            op === "hide"
                ? checked.filter(c => !c.hidden)
                : op === "unhide"
                  ? checked.filter(c => c.hidden)
                  : checked,
        [checked]
    );

    if (isLoading && !assistants.length) return <LoaderView show />;

    return (
        <HeaderLayout blurHeader={false}>
            <div className="assistants-admin">
                <div className="assistants-admin-title">
                    <span className="assistants-admin-title__text" role="heading" aria-level={1}>
                        Assistants
                    </span>
                </div>
                <div className="assistants-admin__actions">
                    <div className="assistants-admin__actions-left">
                        {showBulk && (
                            <ActionMenu
                                label={bulkBusy ? "Working…" : "Action"}
                                disabled={checked.length === 0 || bulkBusy}
                                actions={bulkActions}
                                minWidth={220}
                            />
                        )}
                    </div>
                    <div className="assistants-admin__actions-filters">
                        <CheckboxMenuFilterT
                            label="Type"
                            list={TYPE_FILTER_ITEMS}
                            checkedList={checkedTypes}
                            setCheckedList={setTypeFilter}
                            showCounter={true}
                            rightPosition={true}
                            minWidth={180}
                        />
                    </div>
                    <div className="assistants-admin__actions-right">
                        {canCreate && (
                            <Button
                                className="assistants-admin-create-button"
                                variant="text"
                                startIcon={
                                    <SvgIcon
                                        className="assistants-admin-create-button__icon"
                                        component={PlusIcon}
                                        inheritViewBox
                                    />
                                }
                                onClick={() => setCreateOpen(true)}
                            >
                                Add Assistant
                            </Button>
                        )}
                    </div>
                </div>
                <table className="assistants-admin__table" aria-label="Assistants">
                    <thead>
                        <tr>
                            {showBulk && (
                                <th scope="col" className="assistants-admin__table-select">
                                    <Checkbox
                                        checked={
                                            visibleAssistants.length > 0 &&
                                            checked.length === visibleAssistants.length
                                        }
                                        onChange={toggleSelectAll}
                                        icon={<CheckboxIcon />}
                                        checkedIcon={<CheckboxCheckedIcon />}
                                        inputProps={{ "aria-label": "Select all Assistants" }}
                                    />
                                </th>
                            )}
                            <th scope="col">Name</th>
                            <th scope="col">Documents</th>
                            <th scope="col">Serving agent</th>
                            <th scope="col">
                                <span className="visually-hidden">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleAssistants.map(kb => (
                            <tr key={kb.uid}>
                                {showBulk && (
                                    <td className="assistants-admin__table-select">
                                        <Checkbox
                                            checked={isChecked(kb)}
                                            onChange={() => toggleChecked(kb)}
                                            icon={<CheckboxIcon />}
                                            checkedIcon={<CheckboxCheckedIcon />}
                                            inputProps={{
                                                "aria-label": `Select ${kb.displayName || kb.name}`
                                            }}
                                        />
                                    </td>
                                )}
                                <td>
                                    <div className="assistants-admin__name">
                                        {kb.logoUrl && (
                                            <img
                                                className="assistants-admin__logo"
                                                src={kb.logoUrl}
                                                alt=""
                                            />
                                        )}
                                        <span>{kb.displayName || kb.name}</span>
                                        {kb.hidden && (
                                            <span className="assistants-admin-badge">Hidden</span>
                                        )}
                                    </div>
                                </td>
                                <td>{kb.summary?.learningObjects ?? "—"}</td>
                                <td>{kb.summary?.boundAgent?.name ?? "None (legacy path)"}</td>
                                <td className="assistants-admin__table-actions">
                                    <Tooltip title="Settings">
                                        <span>
                                            <IconButton
                                                className="assistants-admin-action-button"
                                                aria-label={`Edit Assistant ${kb.displayName || kb.name}`}
                                                onClick={() => {
                                                    setEditInitialTab(null);
                                                    setEditTarget(kb);
                                                }}
                                            >
                                                <SvgIcon component={EditIcon} inheritViewBox />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    {canPolicy && (
                                        <Tooltip title="Digestion policy">
                                            <span>
                                                <IconButton
                                                    className="assistants-admin-action-button assistants-admin-action-button--filled"
                                                    aria-label={`Digestion policy for ${kb.displayName || kb.name}`}
                                                    onClick={() => setPolicyTarget(kb)}
                                                >
                                                    <FactCheckOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Files">
                                        <span>
                                            <IconButton
                                                className="assistants-admin-action-button assistants-admin-action-button--filled"
                                                aria-label={`Files for ${kb.displayName || kb.name}`}
                                                onClick={() => openFiles(kb)}
                                            >
                                                <FileCopyOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {policyTarget && companyId && (
                    <DigestionPolicyEditor
                        companyId={companyId}
                        knowledgeBaseUid={policyTarget.uid}
                        knowledgeBaseName={policyTarget.displayName || policyTarget.name}
                        onClose={() => setPolicyTarget(null)}
                    />
                )}
                {editTarget && companyId && (
                    <AssistantEditDialog
                        companyId={companyId}
                        assistant={editTarget}
                        features={{
                            prompt: hasAdminFeature("assistantPrompt"),
                            retrieval: hasAdminFeature("assistantRetrieval"),
                            recognition: hasAdminFeature("processingRecognition"),
                            chunking: hasAdminFeature("processingChunking"),
                            metadata: hasAdminFeature("processingMetadata"),
                            maintenance: hasAdminFeature("processingMaintenance"),
                            // D3: the embedding-model change keeps its stricter
                            // platform super-admin gate server-side — mirror it.
                            embedding: !!isSuperAdmin,
                            invites: hasAdminFeature("inviteLinks")
                        }}
                        initialTab={editInitialTab}
                        onClose={() => setEditTarget(null)}
                        onSaved={() => {
                            setEditTarget(null);
                            reload();
                            loadKnowledgeBases(companyId);
                            // Keep the workspace fresh when the edited
                            // Assistant is the current one.
                            if (user?.currentKnowledgeBaseId === editTarget.uid) {
                                refreshKnowledgeBase();
                                loadKnowledgeBaseConfig(companyId, editTarget.uid);
                            }
                        }}
                    />
                )}
                <ConfirmationPopup
                    open={!!bulkOp}
                    type={bulkOp === "delete" ? ConfirmationPopupType.dangerous : undefined}
                    icon={bulkOp === "delete" ? DeleteIcon : undefined}
                    title={bulkOp ? BULK_META[bulkOp].title : ""}
                    description={
                        bulkOp ? BULK_META[bulkOp].describe(bulkTargets(bulkOp).length) : ""
                    }
                    actionName={bulkOp ? BULK_META[bulkOp].actionName : ""}
                    showCancelButton={bulkOp === "delete"}
                    handleAction={runBulk}
                    handleClose={() => setBulkOp(null)}
                />
                <ModalTemplate
                    open={createOpen}
                    width={600}
                    handleClose={() => setCreateOpen(false)}
                    ariaLabel="Add Assistant"
                >
                    <AddKnowledgeBaseView
                        handleClose={() => setCreateOpen(false)}
                        handleSuccess={() => {
                            setCreateOpen(false);
                            reload();
                            if (companyId) loadKnowledgeBases(companyId);
                        }}
                    />
                </ModalTemplate>
            </div>
        </HeaderLayout>
    );
}
